import React from 'react'
import { Avatar, AvatarGroup, Flex, HStack, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FaEye } from 'react-icons/fa6'
import LoadingDots from '~components/shared/loading-dots'
import type { ChatPresenceMember } from './use-chat-presence'

function formatPresenceCopy(
    members: ChatPresenceMember[],
    verb: 'typing' | 'viewing',
): string {
    const names = members.map(member => member.firstName)
    if (names.length === 1) return `${names[0]} is ${verb}`
    if (names.length === 2) return `${names[0]} and ${names[1]} are ${verb}`
    const [first, second, ...rest] = names
    return `${first}, ${second}, and ${rest.length} other${
        rest.length === 1 ? '' : 's'
    } are ${verb}`
}

function MemberAvatars({ members }: { members: ChatPresenceMember[] }) {
    return (
        <AvatarGroup size="xs" max={3} spacing="-6px">
            {members.map(member => (
                <Avatar
                    key={member.key}
                    name={member.firstName}
                    src={member.avatarUrl ?? undefined}
                />
            ))}
        </AvatarGroup>
    )
}

interface Props {
    typingMembers: ChatPresenceMember[]
    viewingMembers: ChatPresenceMember[]
}

/**
 * Slack-style presence readout rendered above the composer. Typing members
 * get animated dots; viewing-only members get a subtle eye row. Renders
 * nothing when the user is alone in the conversation.
 */
export default function ChatPresenceIndicator({
    typingMembers,
    viewingMembers,
}: Props) {
    if (typingMembers.length === 0 && viewingMembers.length === 0) return null

    return (
        <Flex flexDir="column" gap={1} px={2} pb={2}>
            {typingMembers.length > 0 && (
                <HStack spacing={2}>
                    <MemberAvatars members={typingMembers} />
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        {formatPresenceCopy(typingMembers, 'typing')}
                    </Text>
                    <LoadingDots size="5px" color="gray.500" speed={1.2} />
                </HStack>
            )}
            {viewingMembers.length > 0 && (
                <HStack spacing={2}>
                    <MemberAvatars members={viewingMembers} />
                    <HStack spacing={1}>
                        <Icon as={FaEye} boxSize={3} color="gray.400" />
                        <Text fontSize="xs" color="gray.500">
                            {formatPresenceCopy(viewingMembers, 'viewing')}
                        </Text>
                    </HStack>
                </HStack>
            )}
        </Flex>
    )
}
